import { type InitFlowComponent, useAnalytics, useSettings } from '@capsicum/common';
import { ModalService } from '@capsicum/common-web';
import { atom } from 'jotai';
import React, { useCallback, useEffect, useRef } from 'react';

import { useEeaUser, useGoogleConsentRequirementState } from '../../../shared/src/hooks';
import { GoogleAdsConsentModal } from '../ui/GoogleAdsConsentModal';
import { GoogleAdsConsentModalFooter } from '../ui/GoogleAdsConsentModalFooter';

const isUserDataSharingAllowedAtom = atom(false);
const isAdPersonalizationAllowedAtom = atom(false);

export const GoogleAdsConsentFlow: InitFlowComponent = ({ fallback, isReactive, next }) => {
	const analytics = useAnalytics();
	const internalState = useRef({ isConfirmSent: false }).current;
	const {
		isLoading: isGoogleConsentStateLoading,
		isNotAsked,
		saveState: saveGoogleConsentState
	} = useGoogleConsentRequirementState();
	const { isEeaUser, isLoading: isMeLoading } = useEeaUser();
	const { areSettingsLoading, settings } = useSettings();
	const { isGoogleConsentDialogEnabled } = settings;

	useEffect(() => {
		next();
	}, []);

	const onConfirm = useCallback(
		(isUserDataSharingAllowed: boolean, isAdPersonalizationAllowed: boolean) => {
			analytics.setThirdPartySharing(isUserDataSharingAllowed, isAdPersonalizationAllowed, !!isEeaUser);
			void saveGoogleConsentState(isUserDataSharingAllowed, isAdPersonalizationAllowed);
			internalState.isConfirmSent = true;
		},
		[isEeaUser, saveGoogleConsentState]
	);

	const onDissmiss = useCallback(() => {
		if (!internalState.isConfirmSent) {
			analytics.setThirdPartySharing(false, false, !!isEeaUser);
			void saveGoogleConsentState(false, false);
		}
		internalState.isConfirmSent = false;
	}, [isEeaUser, saveGoogleConsentState]);

	useEffect(() => {
		if (
			isReactive &&
			!isMeLoading &&
			!areSettingsLoading &&
			isGoogleConsentDialogEnabled &&
			!isGoogleConsentStateLoading &&
			isNotAsked
		) {
			ModalService.addModal({
				isClosable: false,
				onDismiss: onDissmiss,
				renderComponent: ({ close }) => (
					<GoogleAdsConsentModal
						closeModal={close}
						isRelevantAdsAllowedAtom={isAdPersonalizationAllowedAtom}
						isUserDataSharingAllowedAtom={isUserDataSharingAllowedAtom}
					/>
				),
				renderFooter: ({ close }) => (
					<GoogleAdsConsentModalFooter
						closeModal={close}
						isRelevantAdsAllowedAtom={isAdPersonalizationAllowedAtom}
						isUserDataSharingAllowedAtom={isUserDataSharingAllowedAtom}
						onPress={onConfirm}
					/>
				)
			});
		}
	}, [isMeLoading, areSettingsLoading, isGoogleConsentDialogEnabled, isGoogleConsentStateLoading, isNotAsked]);

	return fallback;
};

GoogleAdsConsentFlow.keepAlive = true;
